import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import FaceCapture from "@/components/FaceCapture";

export default function EmployeeSignup() {
    const { user, checking, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState("details"); // "details" | "face"
    const [employeeId, setEmployeeId] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (!checking && user) return <Navigate to="/dashboard" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            setError("Passwords don't match");
            return;
        }
        // Step gate: collect face descriptors before submitting.
        setStep("face");
    };

    const completeSignup = async (descriptors) => {
        setError("");
        setLoading(true);
        try {
            const r = await api.post("/auth/employee-signup", {
                employee_id: employeeId.trim().toUpperCase(),
                email: email.trim().toLowerCase(),
                password,
                name: name.trim() || undefined,
                face_descriptors: descriptors,
            });
            await refresh();
            toast.success(`Welcome${r.data.name ? ", " + r.data.name : ""}! Face enrolled successfully.`);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || "Sign-up failed. Please try again.";
            setError(msg);
            setStep("details");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-bone" data-testid="employee-signup-page">
            <div className="hidden lg:block relative overflow-hidden bg-klein-700">
                <img
                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
                    alt="Team at work"
                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-klein-700/70 via-klein/40 to-emerald-700/30" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white flex items-center justify-center rounded-md">
                            <span className="font-display font-black text-klein text-lg">H</span>
                        </div>
                        <div>
                            <div className="font-display font-black text-xl leading-none">Suntosh HRM</div>
                            <div className="text-[11px] uppercase tracking-[0.22em] opacity-80 mt-1">Employee Activation</div>
                        </div>
                    </div>
                    <div>
                        <h2 className="font-display font-black text-4xl leading-[1.05] tracking-tighter">
                            Already on the payroll?<br/>Activate your account.
                        </h2>
                        <p className="mt-5 text-white/85 max-w-md text-sm leading-relaxed">
                            We'll match your email against your HR record. If you're listed as an employee at this company,
                            we'll set up your portal access — for leave requests, payslips, rent receipts, and daily check-in.
                        </p>
                        <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/70">
                            <ShieldCheck className="w-4 h-4" /> Self-service · Geo-fenced attendance · Mobile-friendly
                        </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] opacity-60">© Suntosh HRM</div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8 md:p-16">
                {step === "face" ? (
                    <div className="w-full max-w-md" data-testid="employee-signup-face-step">
                        <div className="overline text-klein">Step · Face enrollment</div>
                        <h1 className="mt-2 font-display font-black tracking-tighter text-3xl text-ink leading-[1.05]">
                            One last thing — your face.
                        </h1>
                        <p className="mt-3 text-sm text-ink/60">
                            Capture three clear shots so we can verify you on daily check-in/out. We store only a 128-number signature, never the photo.
                        </p>
                        <div className="mt-6">
                            <FaceCapture mode="enroll" requiredSamples={3} onComplete={completeSignup} />
                        </div>
                        {loading && <div className="mt-4 text-sm text-ink/50">Submitting…</div>}
                        {error && <div className="mt-4 text-sm text-red-700" data-testid="employee-signup-face-error">{error}</div>}
                        <button
                            type="button"
                            onClick={() => setStep("details")}
                            className="mt-6 text-xs text-ink/50 hover:text-ink"
                        >
                            ← Back to details
                        </button>
                    </div>
                ) : (
                <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="employee-signup-form">
                    <div className="overline text-klein">Step · Welcome</div>
                    <h1 className="mt-2 font-display font-black tracking-tighter text-4xl text-ink leading-[1.05]">
                        Activate your employee account
                    </h1>
                    <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                        Use the email your HR team registered for you (personal or office).
                        You must already exist in the employee directory — ask HR if you're unsure.
                    </p>

                    <div className="mt-10 space-y-7">
                        <div>
                            <label className="overline">Employee ID (staff number)</label>
                            <input
                                type="text"
                                value={employeeId}
                                onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                                required
                                data-testid="employee-signup-employee-id"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink uppercase tracking-wider"
                                placeholder="e.g. ACM1230626232"
                            />
                            <p className="text-xs text-ink/40 mt-1.5">Find this on your offer letter or ask your HR team.</p>
                        </div>
                        <div>
                            <label className="overline">Your email (personal or office)</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="employee-signup-email"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink"
                                placeholder="you@example.com"
                            />
                        </div>
                        <div>
                            <label className="overline">Full name (optional)</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="employee-signup-name"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink"
                                placeholder="As on your employment letter"
                            />
                        </div>
                        <div>
                            <label className="overline">Choose a password</label>
                            <div className="relative">
                                <input
                                    type={showPass ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="employee-signup-password"
                                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 pr-10 px-0 focus:border-klein focus:outline-none text-ink"
                                    placeholder="At least 8 characters"
                                />
                                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink" tabIndex={-1}>
                                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="overline">Confirm password</label>
                            <input
                                type={showPass ? "text" : "password"}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                                data-testid="employee-signup-confirm"
                                className="mt-2 w-full border-b border-ink/20 bg-transparent py-3 px-0 focus:border-klein focus:outline-none text-ink"
                                placeholder="Type it again"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="mt-6 border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive rounded-md" data-testid="employee-signup-error">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="employee-signup-submit"
                        className="mt-8 group w-full bg-klein text-white px-6 py-4 font-medium flex items-center justify-between hover:bg-klein-500 disabled:opacity-60 rounded-md shadow-card hover:shadow-lift"
                    >
                        <span>{loading ? "Activating…" : "Activate my account"}</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>

                    <div className="mt-8 pt-6 border-t border-ink/10 text-center">
                        <p className="text-xs text-ink/50">
                            Already have an account?{" "}
                            <Link to="/login" className="text-klein font-medium hover:underline" data-testid="employee-signup-back-to-login">
                                Sign in →
                            </Link>
                        </p>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}
